/**
 * Control Plane API — Page Config (Page Editor)
 *
 * Split from controlPlaneClient.ts (863 lines, over 800-line limit).
 * Provides page config CRUD for the page customizer editor.
 *
 * Preview token is NOT duplicated here — reuse cpGetPreviewToken()
 * from controlPlaneClient.ts (the same JWT works for both mobile
 * preview and page editor preview).
 */

import type { SectionEnvelope, OverlayElement } from '@zaviago/storefront-config'
import { cpGuestApiCall, getCPToken } from './controlPlaneClient'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageConfigResponse {
  tenantId: string
  pageId: string
  version: string
  sections: SectionEnvelope[]
  overlays?: OverlayElement[]
  seo?: { title?: string; description?: string }
  schemaVersion?: number
  locale?: string
  direction?: 'ltr' | 'rtl'
}

export interface PublishPageConfigPayload {
  page_id: string
  sections_json: string // JSON-serialized PageConfigV2
  meta_title?: string
  meta_description?: string
  theme_json?: string // JSON-serialized theme values for Storefront Theme DocType
  storefront_key?: string // Multi-storefront: target storefront key
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetch page config for the editor.
 * Returns saved config or default template if none exists.
 */
export async function cpGetPageConfig(
  pageId: string = 'home',
  storefrontKey?: string,
): Promise<PageConfigResponse> {
  const token = getCPToken() ?? ''
  const body: Record<string, string> = { access_token: token, page_id: pageId }
  if (storefrontKey) body.storefront_key = storefrontKey
  return cpGuestApiCall<PageConfigResponse>(
    'control_plane.api.page_config.get_config',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

/**
 * Publish page config (sections + SEO metadata).
 */
export async function cpPublishPageConfig(
  payload: PublishPageConfigPayload,
): Promise<PageConfigResponse> {
  const token = getCPToken()
  const body: Record<string, unknown> = { access_token: token, ...payload }
  if (!payload.storefront_key) delete body.storefront_key
  return cpGuestApiCall<PageConfigResponse>(
    'control_plane.api.page_config.publish_config',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

// ---------------------------------------------------------------------------
// Card Design API (card editor — different storage from page config)
// ---------------------------------------------------------------------------

export interface CardTemplateResponse {
  sections: Array<{ type: string; config: Record<string, string> }>
  containerConfig: {
    border: string
    hover: string
    shadow: string
  }
}

/**
 * Fetch current card template from the backend.
 * Returns parsed CardTemplate (not raw Builder blocks).
 */
export async function cpGetCardTemplate(): Promise<CardTemplateResponse> {
  const token = getCPToken()
  return cpGuestApiCall<CardTemplateResponse>(
    'control_plane.api.page_config.get_card_template',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token }),
    },
  )
}

/**
 * Publish card design template.
 * Backend converts to Builder blocks and saves to the system page.
 */
export async function cpPublishCardDesign(
  template: CardTemplateResponse,
): Promise<CardTemplateResponse> {
  const token = getCPToken()
  return cpGuestApiCall<CardTemplateResponse>(
    'control_plane.api.page_config.publish_card_design',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        template_json: JSON.stringify(template),
      }),
    },
  )
}
